import React, { useCallback, useState, useRef, useEffect } from 'react';
import { FileText, Loader2, AlertCircle } from 'lucide-react';
import { PdfViewerModal } from './PdfViewerModal';
import { useWarrantyPdfBlob } from '../hooks/useWarrantyPdfBlob';
import { Document, Page } from '@/Components/shared/react-pdf-lazy';
import './pdf-viewer.css';

interface WarrantyPdfPreviewProps {
  warrantyUuid: string;
  warrantyName?: string;
  headerAction?: React.ReactNode;
}

export const WarrantyPdfPreview: React.FC<WarrantyPdfPreviewProps> = ({
  warrantyUuid,
  warrantyName,
  headerAction,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const { data: pdfBlob, isLoading, error } = useWarrantyPdfBlob(warrantyUuid);

  // Créer et révoquer l'URL du blob dans un seul useEffect
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!pdfBlob) {
      setPdfUrl(null);
      return;
    }
    const url = URL.createObjectURL(pdfBlob);
    setPdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfBlob]);

  // Largeur du conteneur, mesuree par une ref callback plutot que par un
  // `useEffect([])`. Le conteneur n'est rendu qu'une fois `pdfUrl` pose, donc
  // au moment ou un `useEffect([])` s'executait il n'existait pas encore :
  // rien n'etait mesure, et les deps vides empechaient toute reprise. La
  // largeur restait a 0 jusqu'au premier redimensionnement de la fenetre, et
  // <Page> se rendait entre-temps a sa taille naturelle (793 px), rognee par
  // la boite au ratio A4.
  //
  // Une ref callback est appelee quand le noeud entre dans le DOM, avant le
  // premier paint. Le ResizeObserver remplace aussi le listener `resize` :
  // il suit les changements de largeur du conteneur lui-meme, pas seulement
  // ceux de la fenetre.
  const observerRef = useRef<ResizeObserver | null>(null);
  const mesurerConteneur = useCallback((element: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!element) return;
    setContainerWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(element);
    observerRef.current = observer;
  }, []);

  const handlePreviewClick = () => {
    if (pdfUrl) {
      setIsModalOpen(true);
    }
  };

  // Bloquer le clic droit
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  return (
    <>
      {/* Bloc Preview */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-flex tw-flex-col tw-shadow-sm">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FileText className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-m-0">
              Conditions Générales Particulières
            </h3>
          </div>
          {headerAction}
        </div>

        {/* Zone de preview avec ratio A4 */}
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-1">
          {isLoading && (
            <div className="tw-text-center tw-py-8">
              <Loader2 className="tw-w-8 tw-h-8 tw-text-affinicar-primary tw-animate-spin tw-mx-auto tw-mb-2" />
              <p className="tw-text-sm tw-text-gray-600">Chargement du PDF...</p>
            </div>
          )}

          {!!error && (
            <div className="tw-text-center tw-py-8">
              <AlertCircle className="tw-w-8 tw-h-8 tw-text-red-600 tw-mx-auto tw-mb-2" />
              <p className="tw-text-sm tw-text-red-700">Erreur de chargement</p>
            </div>
          )}

          {!isLoading && !error && pdfUrl && (
            <div className="tw-w-full" ref={mesurerConteneur}>
              <div
                onClick={handlePreviewClick}
                onContextMenu={handleContextMenu}
                className="tw-cursor-pointer tw-transition-all hover:tw-scale-[1.02] hover:tw-shadow-lg tw-relative tw-group"
              >
                {/* Overlay au hover */}
                <div className="tw-absolute tw-inset-0 tw-bg-affinicar-primary/0 group-hover:tw-bg-affinicar-primary/10 tw-transition-colors tw-rounded tw-flex tw-items-center tw-justify-center tw-z-10">
                  <div className="tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-bg-affinicar-primary tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-shadow-lg">
                    Cliquer pour voir le document complet
                  </div>
                </div>

                {/* Conteneur avec ratio A4 (1:1.414) */}
                <div 
                  className="tw-bg-white tw-rounded tw-shadow-md tw-overflow-hidden tw-flex tw-items-center tw-justify-center"
                  style={{ aspectRatio: '1 / 1.414' }}
                >
                  <Document
                    file={pdfUrl}
                    loading={
                      <div className="tw-h-full tw-flex tw-items-center tw-justify-center">
                        <Loader2 className="tw-w-6 tw-h-6 tw-text-affinicar-primary tw-animate-spin" />
                      </div>
                    }
                    error={
                      <div className="tw-h-full tw-flex tw-items-center tw-justify-center tw-text-red-600">
                        <p className="tw-text-sm">Erreur</p>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={1}
                      width={containerWidth > 0 ? containerWidth : undefined}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modale de visualisation */}
      <PdfViewerModal
        pdfBlob={pdfBlob || null}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        warrantyName={warrantyName}
      />
    </>
  );
};

